import { useState, useEffect } from 'react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { useAuthStore } from '@/lib/store/authStore'
import { useCustomDomain } from '../settings/useCustomDomain'
import { useDomainPurchase } from '../settings/useDomainPurchase'
import { useDomainSearch } from '../settings/useDomainSearch'
import { DomainProvisioningProgress } from '../settings/DomainProvisioningProgress'
import { DomainManagementPanel } from '../settings/DomainManagementPanel'
import { getCPStorefrontUrl } from '@/lib/api/controlPlaneClient'
import type { DomainSearchResult } from '@/lib/api/controlPlaneClient'
import {
  Search, Check, AlertCircle, Loader2, ExternalLink,
  Trash2, RefreshCw, Copy, Settings, Link2,
} from 'lucide-react'

export function DomainPage() {
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl)
  const authMode = useAuthStore((s) => s.authMode)

  const {
    domain, status, storefrontUrl: domainStorefrontUrl, isLoading,
    setDomainAsync, verify, remove,
    isSettingDomain, isVerifying, isRemoving,
    setDomainError, verifyError, setDomainData,
  } = useCustomDomain()

  const domainPurchase = useDomainPurchase()
  const search = useDomainSearch()

  const [confirmDomain, setConfirmDomain] = useState<{ domain: string; priceUsd: number } | null>(null)
  const [showConnect, setShowConnect] = useState(false)
  const [inputDomain, setInputDomain] = useState('')
  const [showDnsInstructions, setShowDnsInstructions] = useState(false)
  const [showManagement, setShowManagement] = useState(false)

  const defaultSubdomain = getCPStorefrontUrl() || 'your-store.shop.zaviago.com'

  // When purchase completes, clear state
  useEffect(() => {
    if (domainPurchase.isComplete) {
      domainPurchase.reset()
      search.clear()
      setConfirmDomain(null)
    }
  }, [domainPurchase.isComplete]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetDomain = async () => {
    if (!inputDomain.trim()) return
    try {
      await setDomainAsync(inputDomain.trim())
      setShowDnsInstructions(true)
      setShowConnect(false)
    } catch {
      // Error captured in setDomainError
    }
  }

  const handleCopyValue = (value: string) => {
    navigator.clipboard.writeText(value)
  }

  // Guard: not in CP mode
  if (authMode !== 'control_plane' || !storefrontUrl) {
    return (
      <PageContainer>
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-2">Domains</h2>
          <p className="text-muted-foreground">
            {authMode !== 'control_plane'
              ? 'Domain management is available in production mode.'
              : 'Domain management will be available after your storefront is provisioned.'}
          </p>
        </div>
      </PageContainer>
    )
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-6 max-w-2xl">
          <h2 className="text-2xl font-semibold mb-6">Domains</h2>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </PageContainer>
    )
  }

  const isConnected = status === 'active'
  const isPending = status === 'pending_dns' || showDnsInstructions
  const isProvisioningSsl = status === 'provisioning_ssl'
  const isFailed = status === 'failed'
  const hasActiveDomain = isConnected || isPending || isProvisioningSsl || isFailed
  const isPurchasing = !!domainPurchase.purchaseId

  return (
    <PageContainer>
      <div className="p-6 max-w-2xl space-y-8">
        {/* Page title */}
        <div>
          <h2 className="text-2xl font-semibold">Domains</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isConnected
              ? `Your store is live at ${domain}`
              : `Your store is at ${defaultSubdomain}`}
          </p>
        </div>

        {/* ============================================================
            SECTION 1: Active domain management (only if domain exists)
            ============================================================ */}
        {isConnected && (
          <Card className="rounded-lg border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium flex items-center gap-2">
                    {domain}
                    <Check className="w-4 h-4 text-green-500" />
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Active and serving your storefront
                  </p>
                </div>
                <Badge className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400 border-0">
                  Live
                </Badge>
              </div>
              <div className="flex gap-2">
                {domainStorefrontUrl && (
                  <Button variant="outline" asChild className="rounded" size="sm">
                    <a href={domainStorefrontUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      Visit Store
                    </a>
                  </Button>
                )}
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setShowManagement((v) => !v)}
                  className="rounded"
                >
                  <Settings className="w-3.5 h-3.5 mr-1.5" />
                  {showManagement ? 'Hide' : 'Manage'}
                </Button>
              </div>
              {showManagement && (
                <DomainManagementPanel
                  enabled={showManagement}
                  domain={domain!}
                  defaultSubdomain={defaultSubdomain}
                  onRemove={() => { remove(); setShowManagement(false) }}
                  isRemoving={isRemoving}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Pending DNS */}
        {isPending && (
          <Card className="rounded-lg border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{domain || inputDomain}</p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Waiting for DNS configuration...
                  </p>
                </div>
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              </div>
              <DnsInstructionCard
                instructions={setDomainData?.dns_instructions}
                onCopy={handleCopyValue}
              />
              <div className="flex gap-2">
                <Button onClick={() => verify()} disabled={isVerifying} className="rounded" size="sm">
                  {isVerifying ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                  Check DNS
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove()} disabled={isRemoving} className="rounded text-red-500">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Remove
                </Button>
              </div>
              {verifyError && (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {verifyError}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Provisioning SSL */}
        {isProvisioningSsl && (
          <Card className="rounded-lg border-0 shadow-sm">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                <div>
                  <p className="font-medium">{domain}</p>
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Setting up SSL certificate...
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Usually takes 30-60 seconds. This page updates automatically.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Failed */}
        {isFailed && (
          <Card className="rounded-lg border-0 shadow-sm border-red-200 dark:border-red-900">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                <div>
                  <p className="font-medium">{domain}</p>
                  <p className="text-sm text-red-500">Setup failed. Please try again.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => verify()} disabled={isVerifying} className="rounded" size="sm">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Retry
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove()} disabled={isRemoving} className="rounded text-red-500">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Remove
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Purchase in progress */}
        {isPurchasing && (
          <Card className="rounded-lg border-0 shadow-sm">
            <CardContent className="p-6">
              <DomainProvisioningProgress
                domain={domainPurchase.domain ?? confirmDomain?.domain ?? ''}
                stage={domainPurchase.stage ?? 'initiated'}
                stageLabel={domainPurchase.stageLabel ?? 'Starting...'}
                isFailed={domainPurchase.isFailed}
                error={domainPurchase.error}
                onRetry={() => domainPurchase.retry()}
                isRetrying={domainPurchase.isRetrying}
              />
            </CardContent>
          </Card>
        )}

        {/* ============================================================
            SECTION 2: Search hero (always visible when no purchase in progress)
            ============================================================ */}
        {!isPurchasing && (
          <>
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                {hasActiveDomain ? 'Search for another domain' : 'Find your perfect domain'}
              </h3>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-muted-foreground" />
                <Input
                  placeholder="Search domain names..."
                  value={search.rawQuery}
                  onChange={(e) => search.setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && search.searchNow()}
                  className="pl-11 h-12 rounded-md text-base"
                />
              </div>
            </div>

            {/* Search results */}
            <div className="space-y-1">
              {search.error ? (
                <p className="text-sm text-red-500 flex items-center gap-1 py-3">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {search.error}
                </p>
              ) : search.isSearching ? (
                <SearchSkeleton />
              ) : search.hasSearched && search.results.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No domains available for that name. Try something else.
                </p>
              ) : search.results.length > 0 ? (
                <Card className="rounded-md border-0 shadow-sm overflow-hidden">
                  {search.results.map((result, i) => (
                    <ResultRow
                      key={result.domain}
                      result={result}
                      isLast={i === search.results.length - 1}
                      onSelect={(d, price) => setConfirmDomain({ domain: d, priceUsd: price })}
                    />
                  ))}
                </Card>
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Type a name above to check availability across .com, .shop, .store and more
                </p>
              )}
            </div>

            {/* Divider + connect your own */}
            {!hasActiveDomain && (
              <div className="pt-2">
                {!showConnect ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground">or</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                ) : null}

                {!showConnect ? (
                  <button
                    onClick={() => setShowConnect(true)}
                    className="w-full mt-3 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Link2 className="w-3.5 h-3.5" />
                    Already own a domain? Connect it here
                  </button>
                ) : (
                  <Card className="rounded-md border-0 shadow-sm mt-3">
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">Connect Your Domain</p>
                        <button
                          onClick={() => { setShowConnect(false); setInputDomain('') }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="myshop.com"
                          value={inputDomain}
                          onChange={(e) => setInputDomain(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSetDomain()}
                          className="rounded"
                          autoFocus
                        />
                        <Button
                          onClick={handleSetDomain}
                          disabled={isSettingDomain || !inputDomain.trim()}
                          className="rounded"
                          size="sm"
                        >
                          {isSettingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}
                        </Button>
                      </div>
                      {setDomainError && (
                        <p className="text-sm text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {setDomainError}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}

        {/* ============================================================
            Purchase confirmation dialog
            ============================================================ */}
        <AlertDialog open={!!confirmDomain} onOpenChange={(open) => { if (!open) setConfirmDomain(null) }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lg">
                Register {confirmDomain?.domain}?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  <span className="text-2xl font-bold text-foreground">${confirmDomain?.priceUsd}</span>
                  <span className="text-muted-foreground">/year</span>
                </span>
                <span className="block text-sm">
                  Domain will be registered and connected to your storefront automatically.
                  Renews annually.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={domainPurchase.isPurchasing}>
                Cancel
              </AlertDialogCancel>
              <Button
                onClick={() => {
                  if (confirmDomain) {
                    domainPurchase.purchase(confirmDomain.domain)
                    setConfirmDomain(null)
                  }
                }}
                disabled={domainPurchase.isPurchasing}
              >
                {domainPurchase.isPurchasing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  'Purchase Domain'
                )}
              </Button>
            </AlertDialogFooter>
            {domainPurchase.purchaseError && (
              <p className="text-sm text-red-500 flex items-center gap-1 px-6 pb-4">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {domainPurchase.purchaseError}
              </p>
            )}
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PageContainer>
  )
}

/* ── Sub-components ──────────────────────────────────────────── */

function ResultRow({
  result,
  isLast,
  onSelect,
}: {
  result: DomainSearchResult
  isLast: boolean
  onSelect: (domain: string, priceUsd: number) => void
}) {
  const { domain, available, price_usd, premium } = result

  return (
    <div
      className={`flex items-center justify-between px-5 py-3.5 transition-colors ${
        available ? 'hover:bg-muted/40' : 'opacity-50'
      } ${!isLast ? 'border-b border-border/50' : ''}`}
    >
      <div className="flex items-center gap-2.5">
        {available && <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />}
        <span className={`font-medium ${available ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
          {domain}
        </span>
        {premium && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Premium</Badge>
        )}
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm tabular-nums text-muted-foreground">
          ${price_usd}/yr
        </span>
        {available ? (
          <Button
            size="sm"
            className="rounded-sm h-8 px-3 text-xs"
            onClick={() => onSelect(domain, price_usd)}
          >
            Get This
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground w-[62px] text-center">Taken</span>
        )}
      </div>
    </div>
  )
}

function SearchSkeleton() {
  return (
    <Card className="rounded-md border-0 shadow-sm overflow-hidden">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className={`flex items-center justify-between px-5 py-3.5 ${i < 4 ? 'border-b border-border/50' : ''}`}>
          <div className="h-4 w-36 bg-muted rounded-md animate-pulse" />
          <div className="flex items-center gap-3">
            <div className="h-4 w-14 bg-muted rounded-md animate-pulse" />
            <div className="h-8 w-16 bg-muted rounded-md animate-pulse" />
          </div>
        </div>
      ))}
    </Card>
  )
}

function DnsInstructionCard({
  instructions,
  onCopy,
}: {
  instructions?: { a_record?: { name?: string }; note?: string }
  onCopy: (value: string) => void
}) {
  return (
    <Card className="rounded-md bg-muted/50 border-0">
      <CardContent className="p-4 space-y-3">
        <p className="text-sm font-medium">Add this DNS record at your domain provider:</p>
        <div className="space-y-2 text-sm font-mono">
          <DnsRow label="Type" value="A" onCopy={onCopy} />
          <DnsRow label="Name" value={instructions?.a_record?.name || '@'} onCopy={onCopy} />
          <DnsRow label="Value" value="143.198.201.131" onCopy={onCopy} />
        </div>
        {instructions?.note && (
          <p className="text-xs text-muted-foreground">{instructions.note}</p>
        )}
      </CardContent>
    </Card>
  )
}

function DnsRow({ label, value, onCopy }: { label: string; value: string; onCopy: (v: string) => void }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-muted-foreground">{label}:</span>
      <span className="flex items-center gap-1">
        {value}
        <button onClick={() => onCopy(value)} className="p-0.5 hover:bg-muted rounded-md">
          <Copy className="w-3 h-3 text-muted-foreground" />
        </button>
      </span>
    </div>
  )
}
